
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  ShieldAlert,
  Shield,
  KeyRound,
  Copy,
  Check,
  LoaderCircle,
  RefreshCw,
  Eye,
  EyeOff,
  Smartphone,
  LockKeyhole,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  X,
} from "lucide-react";
import api from "../services/api";
import "./Configuracoes2FA.css";

const Configuracoes2FA = () => {
  const navigate = useNavigate();

  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [token2FA, setToken2FA] = useState("");
  const [tokenDesabilitar, setTokenDesabilitar] = useState("");

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingSetup, setLoadingSetup] = useState(false);
  const [loadingEnable, setLoadingEnable] = useState(false);
  const [loadingDisable, setLoadingDisable] = useState(false);

  const [modalDesabilitar, setModalDesabilitar] = useState(false);
  const [mostrarSecret, setMostrarSecret] = useState(false);
  const [mostrarTokenDesabilitar, setMostrarTokenDesabilitar] =
    useState(false);
  const [copiado, setCopiado] = useState("");

  const limparMensagens = () => {
    setErro("");
    setSucesso("");
  };

  const obterErro = (err, fallback) => {
    return (
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      fallback
    );
  };

  const obterEmail = () => {
    return sessionStorage.getItem("email");
  };

  const carregarStatus = useCallback(async () => {
    setLoadingStatus(true);
    setErro("");

    try {
      const email = obterEmail();

      if (!email) {
        setErro("Sua sessão não foi identificada. Entre novamente.");
        return;
      }

      const response = await api.get("/check-2fa-status", {
        params: { email },
      });

      setIs2FAEnabled(Boolean(response.data.two_factor_enabled));
    } catch (err) {
      console.error("Erro ao consultar status do 2FA.");
      setErro(obterErro(err, "Não foi possível consultar o status do 2FA."));
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    carregarStatus();
  }, [carregarStatus]);

  const handleSetup2FA = async () => {
    limparMensagens();

    const email = obterEmail();

    if (!email) {
      setErro("Sua sessão não foi identificada. Entre novamente.");
      return;
    }

    setLoadingSetup(true);

    try {
      const status = await api.get("/check-2fa-status", {
        params: { email },
      });

      if (status.data.two_factor_enabled) {
        setIs2FAEnabled(true);
        setErro("A autenticação em duas etapas já está habilitada.");
        return;
      }

      const response = await api.post("/setup-2fa", { email });

      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
      setBackupCodes(response.data.backup_codes || []);
      setToken2FA("");
      setMostrarSecret(false);
      setSucesso("Leia o QR Code no aplicativo autenticador e confirme o código.");
    } catch (err) {
      console.error("Erro ao iniciar configuração do 2FA.");
      setErro(obterErro(err, "Não foi possível iniciar a configuração do 2FA."));
    } finally {
      setLoadingSetup(false);
    }
  };

  const handleEnable2FA = async (event) => {
    event.preventDefault();
    limparMensagens();

    const email = obterEmail();

    if (!email) {
      setErro("Sua sessão não foi identificada. Entre novamente.");
      return;
    }

    const token = token2FA.replace(/\s/g, "");

    if (!/^\d{6}$/.test(token)) {
      setErro("Digite o código de 6 dígitos do aplicativo autenticador.");
      return;
    }

    setLoadingEnable(true);

    try {
      const response = await api.post("/enable-2fa", {
        email,
        token_2fa: token,
      });

      if (response.status === 200) {
        setIs2FAEnabled(true);
        setSucesso("Autenticação em duas etapas habilitada com sucesso!");
        setQrCode("");
        setSecret("");
        setToken2FA("");
        setMostrarSecret(false);
      }
    } catch (err) {
      setErro(obterErro(err, "Código inválido ou não foi possível habilitar o 2FA."));
    } finally {
      setLoadingEnable(false);
    }
  };

  const handleDisable2FA = async (event) => {
    event.preventDefault();
    limparMensagens();

    const email = obterEmail();

    if (!email) {
      setErro("Sua sessão não foi identificada. Entre novamente.");
      return;
    }

    const token = tokenDesabilitar.replace(/\s/g, "");

    if (!token) {
      setErro("Informe seu código autenticador para continuar.");
      return;
    }

    setLoadingDisable(true);

    try {
      const response = await api.post("/disable-2fa", {
        email,
        token_2fa: token,
      });

      if (response.status === 200) {
        setIs2FAEnabled(false);
        setModalDesabilitar(false);
        setTokenDesabilitar("");
        setQrCode("");
        setSecret("");
        setBackupCodes([]);
        setToken2FA("");
        setSucesso("Autenticação em duas etapas desabilitada.");
      }
    } catch (err) {
      setErro(obterErro(err, "Código inválido ou não foi possível desabilitar o 2FA."));
    } finally {
      setLoadingDisable(false);
    }
  };

  const copiarTexto = async (texto, identificador) => {
    limparMensagens();

    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(identificador);
      window.setTimeout(() => setCopiado(""), 1800);
    } catch {
      setErro(
        "Não foi possível copiar automaticamente. Selecione e copie o conteúdo manualmente."
      );
    }
  };

  const cancelarConfiguracao = () => {
    setQrCode("");
    setSecret("");
    setBackupCodes([]);
    setToken2FA("");
    setMostrarSecret(false);
    limparMensagens();
  };

  return (
    <main className="ca2fa">
      <div className="ca2fa__container">
        <button
          type="button"
          className="ca2fa__back"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={17} />
          Voltar
        </button>

        <header className="ca2fa__header">
          <div className="ca2fa__header-icon">
            <ShieldCheck size={29} />
          </div>

          <div>
            <span className="ca2fa__eyebrow">CONTA E SEGURANÇA</span>
            <h1>Autenticação em duas etapas</h1>
            <p>
              Adicione uma camada extra de proteção à sua conta Cronos Agenda.
            </p>
          </div>
        </header>

        {erro && (
          <div className="ca2fa__alert ca2fa__alert--error" role="alert">
            <AlertCircle size={19} />
            <span>{erro}</span>
            <button
              type="button"
              aria-label="Fechar mensagem"
              onClick={() => setErro("")}
            >
              <X size={17} />
            </button>
          </div>
        )}

        {sucesso && (
          <div className="ca2fa__alert ca2fa__alert--success" role="status">
            <CheckCircle2 size={19} />
            <span>{sucesso}</span>
            <button
              type="button"
              aria-label="Fechar mensagem"
              onClick={() => setSucesso("")}
            >
              <X size={17} />
            </button>
          </div>
        )}

        <section className="ca2fa__status-card">
          <div className="ca2fa__status-icon">
            {loadingStatus ? (
              <LoaderCircle className="ca2fa__spin" size={26} />
            ) : is2FAEnabled ? (
              <ShieldCheck size={27} />
            ) : (
              <ShieldAlert size={27} />
            )}
          </div>

          <div className="ca2fa__status-content">
            <span className="ca2fa__muted-label">STATUS DA PROTEÇÃO</span>
            <h2>
              {loadingStatus
                ? "Verificando segurança..."
                : is2FAEnabled
                  ? "Sua conta está protegida"
                  : "Proteção adicional desativada"}
            </h2>
            <p>
              {loadingStatus
                ? "Consultando as configurações da sua conta."
                : is2FAEnabled
                  ? "Um código temporário do aplicativo autenticador é exigido conforme as regras de autenticação da sua conta."
                  : "Habilite o segundo fator para dificultar acessos indevidos, mesmo que sua senha seja descoberta."}
            </p>
          </div>

          {!loadingStatus && (
            <span
              className={`ca2fa__badge ${
                is2FAEnabled
                  ? "ca2fa__badge--active"
                  : "ca2fa__badge--inactive"
              }`}
            >
              <span />
              {is2FAEnabled ? "Habilitado" : "Desabilitado"}
            </span>
          )}
        </section>

        <section className="ca2fa__panel">
          <div className="ca2fa__panel-heading">
            <div className="ca2fa__panel-icon">
              <Smartphone size={22} />
            </div>
            <div>
              <h2>Configurar autenticador</h2>
              <p>
                Use um aplicativo compatível com códigos TOTP, como Google
                Authenticator ou Microsoft Authenticator.
              </p>
            </div>
          </div>

          {loadingStatus ? (
            <div className="ca2fa__loading">
              <LoaderCircle className="ca2fa__spin" size={22} />
              Carregando configurações...
            </div>
          ) : is2FAEnabled ? (
            <div className="ca2fa__enabled-content">
              <div className="ca2fa__enabled-message">
                <CheckCircle2 size={22} />
                <div>
                  <strong>Autenticação configurada</strong>
                  <p>
                    Sua conta já está utilizando a proteção em duas etapas.
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="ca2fa__button ca2fa__button--danger-outline"
                onClick={() => {
                  limparMensagens();
                  setModalDesabilitar(true);
                }}
                disabled={loadingDisable}
              >
                <ShieldAlert size={17} />
                Desabilitar 2FA
              </button>
            </div>
          ) : !qrCode ? (
            <div className="ca2fa__setup-intro">
              <div className="ca2fa__steps">
                <div className="ca2fa__step">
                  <span>1</span>
                  <div>
                    <strong>Prepare o aplicativo</strong>
                    <p>Abra o autenticador no seu celular.</p>
                  </div>
                </div>
                <div className="ca2fa__step">
                  <span>2</span>
                  <div>
                    <strong>Leia o QR Code</strong>
                    <p>Vincule o autenticador à sua conta.</p>
                  </div>
                </div>
                <div className="ca2fa__step">
                  <span>3</span>
                  <div>
                    <strong>Confirme a configuração</strong>
                    <p>Digite o código temporário gerado pelo aplicativo.</p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="ca2fa__button ca2fa__button--primary"
                onClick={handleSetup2FA}
                disabled={loadingSetup}
              >
                {loadingSetup ? (
                  <LoaderCircle className="ca2fa__spin" size={18} />
                ) : (
                  <LockKeyhole size={18} />
                )}
                {loadingSetup ? "Preparando..." : "Configurar 2FA"}
              </button>
            </div>
          ) : (
            <div className="ca2fa__configuration">
              <div className="ca2fa__notice">
                <AlertCircle size={19} />
                <p>
                  Não feche esta etapa antes de vincular o aplicativo e
                  confirmar o código. Guarde os códigos de recuperação em
                  local seguro.
                </p>
              </div>

              <div className="ca2fa__qr-card">
                <div className="ca2fa__qr-frame">
                  <img src={qrCode} alt="QR Code para configurar o autenticador" />
                </div>
                <h3>Leia o QR Code</h3>
                <p>
                  No aplicativo autenticador, escolha a opção para adicionar
                  uma conta e escaneie esta imagem.
                </p>

                {secret && (
                  <div className="ca2fa__secret">
                    <span>Chave de configuração manual</span>
                    <div className="ca2fa__secret-value">
                      <code>{mostrarSecret ? secret : "••••••••••••••••"}</code>
                      <button
                        type="button"
                        aria-label={mostrarSecret ? "Ocultar chave" : "Mostrar chave"}
                        onClick={() => setMostrarSecret((value) => !value)}
                      >
                        {mostrarSecret ? <EyeOff size={17} /> : <Eye size={17} />}
                      </button>
                      <button
                        type="button"
                        aria-label="Copiar chave"
                        onClick={() => copiarTexto(secret, "secret")}
                      >
                        {copiado === "secret" ? (
                          <Check size={17} />
                        ) : (
                          <Copy size={17} />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <form className="ca2fa__form" onSubmit={handleEnable2FA}>
                <label htmlFor="token2fa">Código de verificação</label>
                <p>Digite o código de 6 dígitos exibido no autenticador.</p>
                <input
                  id="token2fa"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={token2FA}
                  onChange={(event) =>
                    setToken2FA(event.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  required
                />

                <button
                  type="submit"
                  className="ca2fa__button ca2fa__button--primary ca2fa__button--full"
                  disabled={loadingEnable || token2FA.length !== 6}
                >
                  {loadingEnable ? (
                    <LoaderCircle className="ca2fa__spin" size={18} />
                  ) : (
                    <ShieldCheck size={18} />
                  )}
                  {loadingEnable ? "Verificando..." : "Confirmar e habilitar"}
                </button>
              </form>

              {backupCodes.length > 0 && (
                <div className="ca2fa__backup">
                  <div className="ca2fa__backup-heading">
                    <div className="ca2fa__panel-icon">
                      <KeyRound size={21} />
                    </div>
                    <div>
                      <h3>Códigos de recuperação</h3>
                      <p>
                        Use-os somente se perder acesso ao autenticador.
                        Cada código pode ter regras de uso definidas pelo servidor.
                      </p>
                    </div>
                  </div>

                  <div className="ca2fa__backup-warning">
                    Guarde estes códigos agora. Eles podem não ser exibidos
                    novamente.
                  </div>

                  <div className="ca2fa__backup-list">
                    {backupCodes.map((code, index) => (
                      <code key={`${index}-${code}`}>{code}</code>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="ca2fa__button ca2fa__button--secondary"
                    onClick={() =>
                      copiarTexto(backupCodes.join("\n"), "backup")
                    }
                  >
                    {copiado === "backup" ? (
                      <Check size={17} />
                    ) : (
                      <Copy size={17} />
                    )}
                    {copiado === "backup" ? "Códigos copiados" : "Copiar códigos"}
                  </button>
                </div>
              )}

              <button
                type="button"
                className="ca2fa__cancel"
                onClick={cancelarConfiguracao}
                disabled={loadingEnable}
              >
                Cancelar configuração
              </button>
            </div>
          )}
        </section>

        <section className="ca2fa__tip">
          <Shield size={20} />
          <div>
            <strong>Dica de segurança</strong>
            <p>
              Nunca compartilhe códigos de autenticação ou recuperação. A equipe
              do Cronos Agenda não precisa deles para prestar suporte.
            </p>
          </div>
        </section>

        <div className="ca2fa__footer">
          <button
            type="button"
            onClick={carregarStatus}
            disabled={loadingStatus}
          >
            <RefreshCw size={15} />
            Atualizar status
          </button>
        </div>
      </div>

      {modalDesabilitar && (
        <div
          className="ca2fa__modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !loadingDisable) {
              setModalDesabilitar(false);
              setTokenDesabilitar("");
            }
          }}
        >
          <section
            className="ca2fa__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal2fa-title"
          >
            <div className="ca2fa__modal-icon">
              <ShieldAlert size={25} />
            </div>

            <button
              type="button"
              className="ca2fa__modal-close"
              aria-label="Fechar"
              disabled={loadingDisable}
              onClick={() => {
                setModalDesabilitar(false);
                setTokenDesabilitar("");
                limparMensagens();
              }}
            >
              <X size={19} />
            </button>

            <h2 id="modal2fa-title">Desabilitar autenticação?</h2>
            <p>
              Sua conta ficará sem a camada adicional de proteção. Para
              confirmar, informe o código atual do autenticador.
            </p>

            <form onSubmit={handleDisable2FA}>
              <label htmlFor="tokenDesabilitar">Código autenticador</label>
              <div className="ca2fa__password-input">
                <input
                  id="tokenDesabilitar"
                  type={mostrarTokenDesabilitar ? "text" : "password"}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={tokenDesabilitar}
                  onChange={(event) =>
                    setTokenDesabilitar(event.target.value.replace(/\D/g, "").slice(0, 8))
                  }
                  placeholder="Digite seu código"
                  required
                />
                <button
                  type="button"
                  aria-label={mostrarTokenDesabilitar ? "Ocultar código" : "Mostrar código"}
                  onClick={() =>
                    setMostrarTokenDesabilitar((value) => !value)
                  }
                >
                  {mostrarTokenDesabilitar ? (
                    <EyeOff size={17} />
                  ) : (
                    <Eye size={17} />
                  )}
                </button>
              </div>

              <div className="ca2fa__modal-actions">
                <button
                  type="button"
                  className="ca2fa__button ca2fa__button--secondary"
                  disabled={loadingDisable}
                  onClick={() => {
                    setModalDesabilitar(false);
                    setTokenDesabilitar("");
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="ca2fa__button ca2fa__button--danger"
                  disabled={loadingDisable || !tokenDesabilitar}
                >
                  {loadingDisable && (
                    <LoaderCircle className="ca2fa__spin" size={17} />
                  )}
                  Confirmar desativação
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </main>
  );
};

export default Configuracoes2FA;
